import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { formatResponse, parseTodoPlan, parseContentWithWidgets } from '../utils/formatResponse';
import TodoPlanCard from './TodoPlanCard';
import Todo from './Todo';
import ArticleCard from './ArticleCard';
import NewsCard from './NewsCard';
import TestimonialCard from './TestimonialCard';
import PartnerCard from './PartnerCard';
import VideoCard from './VideoCard';
import CoachCard from './CoachCard';
import Quiz from './Quiz';
import ProfileQuestion from './ProfileQuestion';
import DisclaimerBanner from './DisclaimerBanner';
import Tip from './Tip';
import CelebrationBubble from './CelebrationBubble';
import AnimatedMessage from './AnimatedMessage';
// BeadUnlockModal is now handled globally in App.jsx via beadAwarded events
import { todosAPI } from '../services/api';
import stateManager from '../services/stateManager';
import styles from '../styles/MessageBubble.module.css';

// Wrapper component for cards that auto-advances after animation completes
function AnimatedCard({ children, onComplete, skipAnimation }) {
  useEffect(() => {
    // If skipping animation (already shown), don't call onComplete
    if (skipAnimation) {
      return;
    }

    // Only set timer if onComplete is provided (means this is the current revealing card)
    if (!onComplete) {
      return;
    }

    // Wait for card animation to complete (400ms) + brief pause (300ms)
    const timer = setTimeout(() => {
      onComplete();
    }, 700);

    return () => clearTimeout(timer);
  }, [onComplete, skipAnimation]);

  return (
    <div className={skipAnimation ? styles.cardComplete : styles.cardAppear}>
      {children}
    </div>
  );
}

// Advances the part reveal almost immediately — for parts that should render
// with no card pacing (the disclaimer banner must appear before the message).
function InstantAdvance({ onComplete }) {
  useEffect(() => {
    if (!onComplete) return;
    const timer = setTimeout(onComplete, 150);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return null;
}

/**
 * instantReveal: render the finished message immediately, no word-by-word
 * animation. Used when the reply already STREAMED in live — the user watched
 * every word arrive, so re-animating the same text read as "it loaded, then
 * loaded again". Unlike isHistory it keeps the normal bubble styling and
 * still fires onRevealComplete.
 */
function MessageBubble({ role, content, timestamp, conversationId, currentMission, onPlanApprove, onPlanEdit, onArticleClick, onNewsClick, onCoachClick, onQuizComplete, onRevealComplete, isHistory = false, instantReveal = false }) {
  // Track todos state for re-rendering when singleton updates
  const [cachedTodos, setCachedTodos] = useState(stateManager.getTodos() || []);
  // Note: Bead modals are now handled globally in App.jsx via beadAwarded events

  // Track which part is currently being animated (simplified approach)
  const [currentPartIndex, setCurrentPartIndex] = useState(0);

  // Track the content we're currently animating to detect actual changes
  const lastContentRef = useRef(null);

  // Ref to track the last rendered part for auto-scrolling
  const lastPartRef = useRef(null);

  // Subscribe to todos changes from singleton
  useEffect(() => {
    const unsubscribe = stateManager.subscribe('todos', (updatedTodos) => {
      // console.log('[MessageBubble] Received todos update from singleton');
      setCachedTodos(updatedTodos);
    });

    return unsubscribe;
  }, []);

  // Parse content once and memoize to prevent unnecessary re-parsing
  // This ensures the parts array has stable references across re-renders
  const contentParts = useMemo(() => {
    return parseContentWithWidgets(content);
  }, [content]);

  // TodoPlan messages render synchronously; detect once and fire reveal-complete
  // from an effect so the parent setState doesn't run during render.
  const hasTodoPlan = useMemo(() => /<TodoPlan:\{[\s\S]*?\}>/.test(content || ''), [content]);

  // A message that is ONLY a celebration bubble (a delivered queued fact) is
  // rendered standalone and centered — never inside the assistant bubble
  // chrome. The server always saves celebrations as their own message.
  const isCelebrationOnly = useMemo(() =>
    contentParts.parts.length === 1 && contentParts.parts[0].type === 'celebration',
  [contentParts]);

  // Track onRevealComplete via ref so we can fire it from effects without
  // re-running them on every parent re-render.
  const onRevealCompleteRef = useRef(onRevealComplete);
  useEffect(() => {
    onRevealCompleteRef.current = onRevealComplete;
  }, [onRevealComplete]);
  const revealFiredRef = useRef(false);

  const fireRevealComplete = useCallback(() => {
    if (revealFiredRef.current || isHistory) return;
    revealFiredRef.current = true;
    if (onRevealCompleteRef.current) onRevealCompleteRef.current();
  }, [isHistory]);

  // Reset animation when content ACTUALLY changes (new message)
  // Use ref to avoid re-triggering on every render
  useEffect(() => {
    const contentChanged = lastContentRef.current !== content;

    if (contentChanged) {
      lastContentRef.current = content;
      setCurrentPartIndex(0);
      revealFiredRef.current = false;
    }
  }, [content]);

  // Widget-based messages: animation is done once we've advanced past the
  // last part (handlePartComplete bumps currentPartIndex past parts.length).
  useEffect(() => {
    if (isHistory) return;
    if (!contentParts.hasWidgets) return;
    if (currentPartIndex >= contentParts.parts.length) {
      fireRevealComplete();
    }
  }, [contentParts, currentPartIndex, isHistory, fireRevealComplete]);

  // TodoPlan path renders synchronously — no animation to wait on.
  useEffect(() => {
    if (isHistory) return;
    if (hasTodoPlan) fireRevealComplete();
  }, [hasTodoPlan, isHistory, fireRevealComplete]);

  // Standalone celebration bubble also renders synchronously.
  useEffect(() => {
    if (isHistory) return;
    if (isCelebrationOnly) fireRevealComplete();
  }, [isCelebrationOnly, isHistory, fireRevealComplete]);

  // Instant reveal: everything is already on screen, so complete right away.
  useEffect(() => {
    if (instantReveal && !isHistory) fireRevealComplete();
  }, [instantReveal, isHistory, fireRevealComplete]);

  // Handle part completion - move to next part
  // useCallback with NO dependencies to keep reference stable across re-renders
  // We use the functional form of setState (prev => prev + 1) to avoid needing currentPartIndex in deps
  const handlePartComplete = useCallback(() => {
    setCurrentPartIndex(prev => prev + 1);
  }, []); // Empty deps - function never changes!

  // Auto-scroll to keep the last revealed part in view. The window never
  // scrolls in this app (the chat area is its own overflow container), so we
  // pin the nearest REAL scroll ancestor (overflow-y auto/scroll) to the
  // bottom — same walk AnimatedMessage uses for its word ticks.
  useEffect(() => {
    const node = lastPartRef.current;
    if (!node) return;
    let el = node.parentElement;
    while (el) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) break;
      el = el.parentElement;
    }
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [currentPartIndex]);

  // Handle todo toggle in chat view
  const handleTodoToggle = async ({ todoId, onComplete, onError }) => {
    try {
      // console.log('[MessageBubble] Toggling todo:', todoId);

      // Call API - bead modals are now handled globally via beadAwarded events in App.jsx
      await todosAPI.toggleTodo(todoId);
      // console.log('[MessageBubble] API call successful');

      // Refresh todos from server to get actual state
      const freshTodos = await todosAPI.getTodos(conversationId);
      // console.log('[MessageBubble] Refreshed todos from server');

      // Update singleton cache
      stateManager.setTodos(freshTodos);

      // Notify success
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('[MessageBubble] Failed to toggle todo:', error);

      // Notify error
      if (onError) {
        onError(error.message || 'Failed to update todo');
      }
    }
  };
  if (role === 'user') {
    return (
      <div className={isHistory ? styles.userMessageHistory : styles.userMessage}>
        <div className={isHistory ? styles.userBubbleHistory : styles.userBubble}>
          {content}
        </div>
      </div>
    );
  }

  if (role === 'assistant') {
    // Check if this message contains a TodoPlan
    const todoPlan = parseTodoPlan(content);

    if (todoPlan) {
      // todoPlan is now { objective: "...", tiers: [...] }
      // Extract text before and after the TodoPlan tag
      const todoPlanRegex = /<TodoPlan:\{[\s\S]*?\}>/;
      const parts = content.split(todoPlanRegex);
      const textBefore = parts[0] ? formatResponse(parts[0].trim()) : '';
      const textAfter = parts[1] ? formatResponse(parts[1].trim()) : '';

      // Render text + TodoPlanCard + text
      return (
        <div className={styles.assistantMessage}>
          <div className={styles.assistantContent}>
            {textBefore && (
              <div
                className={styles.assistantBubble}
                dangerouslySetInnerHTML={{ __html: textBefore }}
              />
            )}
            <TodoPlanCard
              plan={todoPlan.tiers}
              objective={todoPlan.objective}
              oldObjective={currentMission}
              conversationId={conversationId}
              onApprove={onPlanApprove}
              onApproved={onPlanApprove}
              onEdit={onPlanEdit}
              onCancel={onPlanEdit ? () => onPlanEdit("I'll keep my current plan, thanks.") : undefined}
            />
            {textAfter && (
              <div
                className={styles.assistantBubble}
                dangerouslySetInnerHTML={{ __html: textAfter }}
              />
            )}
          </div>
        </div>
      );
    }

    // A celebration-only message renders as its own centered bubble — no
    // assistant chrome, same look live and in history.
    if (isCelebrationOnly) {
      const celebration = contentParts.parts[0];
      return (
        <CelebrationBubble
          factType={celebration.data.factType}
          message={celebration.data.message}
          progress={celebration.data.progress}
        />
      );
    }

    // Check if message contains interactive widgets/cards
    // contentParts is already parsed and memoized at the top of the component

    if (contentParts.hasWidgets) {
      // Use live todos from state (subscribed to singleton changes)
      const todosById = {};
      cachedTodos.forEach(stage => {
        stage.todos.forEach(todo => {
          todosById[todo.id] = todo;
        });
      });

      // For history and instant reveal: show ALL parts immediately, no animation.
      // (instantReveal disables the per-part onComplete chain, so slicing by
      // currentPartIndex would strand every part after the first — e.g. a
      // greeting's text showing but its Video card never appearing on reload.)
      // For a live message: render parts sequentially with animation.
      const visibleParts = (isHistory || instantReveal)
        ? contentParts.parts
        : contentParts.parts.slice(0, currentPartIndex + 1);

      return (
        <div className={isHistory ? styles.assistantMessageHistory : styles.assistantMessage}>
          <div className={isHistory ? styles.assistantContentHistory : styles.assistantContent}>
            {visibleParts.map((part, index) => {
              // For history and just-streamed replies: skip all animations
              const isAnimating = (isHistory || instantReveal) ? false : index === currentPartIndex;
              const isComplete = (isHistory || instantReveal) ? true : index < currentPartIndex;
              const isLast = index === visibleParts.length - 1;

              if (part.type === 'text') {
                // Use AnimatedMessage for text parts (or direct render for history)
                const formattedText = formatResponse(part.content);
                if (isHistory) {
                  // History: render directly without animation
                  return (
                    <div key={index} dangerouslySetInnerHTML={{ __html: formattedText }} />
                  );
                }
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedMessage
                      content={formattedText}
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    />
                  </div>
                );
              } else if (part.type === 'widget') {
                // IMPORTANT: Use widget tag data (most recent), NOT cached singleton data
                // Widget tags reflect the exact state at the time of the message
                // Singleton cache may be stale if todos haven't been refetched yet
                const isCompleted = part.data.isCompleted;
                const isOptional = part.data.isOptional;
                const todoText = part.data.todoText;

                return (
                  <div key={part.data.todoId} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <Todo
                        todoId={part.data.todoId}
                        todoText={todoText}
                        isCompleted={isCompleted}
                        isOptional={isOptional}
                        variant="chat"
                        onToggle={handleTodoToggle}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'article') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <ArticleCard
                      title={part.data.title}
                      slug={part.data.slug}
                      category={part.data.category}
                      excerpt={part.data.excerpt}
                      heroImage={part.data.heroImage}
                      onClick={onArticleClick}
                    />
                  </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'news') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <NewsCard
                        id={part.data.id}
                        headline={part.data.headline}
                        sourceUrl={part.data.sourceUrl}
                        publishedDate={part.data.publishedDate}
                        summary={part.data.summary}
                        heroImage={part.data.heroImage}
                        onClick={onNewsClick}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'testimonial') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <TestimonialCard
                        userName={part.data.userName}
                        achievement={part.data.achievement}
                        testimonialText={part.data.testimonialText}
                        imageUrl={part.data.imageUrl}
                        date={part.data.date}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'partner') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <PartnerCard
                        name={part.data.name}
                        websiteUrl={part.data.websiteUrl}
                        bestFor={part.data.bestFor}
                        imageUrl={part.data.imageUrl}
                        description={part.data.description}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'video') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <VideoCard
                        id={part.data.id}
                        videoId={part.data.videoId}
                        title={part.data.title}
                        courseName={part.data.courseName}
                        description={part.data.description}
                        videoUrl={part.data.videoUrl}
                        lessonUrl={part.data.lessonUrl}
                        thumbnailUrl={part.data.thumbnailUrl}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'coach') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <CoachCard
                        id={part.data.id}
                        displayName={part.data.displayName}
                        bio={part.data.bio}
                        specializations={part.data.specializations}
                        sessionDuration={part.data.sessionDuration}
                        profileImage={part.data.profileImage}
                        similarity={part.data.similarity}
                        onSelect={onCoachClick}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'quiz') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <Quiz
                        slug={part.data.slug}
                        title={part.data.title}
                        description={part.data.description}
                        estimatedMinutes={part.data.estimatedMinutes}
                        variant="chat"
                        onComplete={onQuizComplete}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'disclaimer') {
                // Billboard: renders immediately (no AnimatedCard pacing) so it
                // genuinely appears before the message content
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <DisclaimerBanner />
                    {isAnimating && <InstantAdvance onComplete={handlePartComplete} />}
                  </div>
                );
              } else if (part.type === 'celebration') {
                // Fallback for a celebration mixed into a larger message
                // (server normally saves celebrations standalone — see above)
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <CelebrationBubble
                        factType={part.data.factType}
                        message={part.data.message}
                        progress={part.data.progress}
                      />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'tip') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <Tip text={part.data.text} />
                    </AnimatedCard>
                  </div>
                );
              } else if (part.type === 'profilequestion') {
                return (
                  <div key={index} ref={isLast ? lastPartRef : null}>
                    <AnimatedCard
                      onComplete={isAnimating ? handlePartComplete : undefined}
                      skipAnimation={isComplete}
                    >
                      <ProfileQuestion
                        field={part.data.field}
                        question={part.data.question}
                        type={part.data.type}
                        options={part.data.options}
                        allowCustom={part.data.allowCustom}
                        savesToProfile={part.data.savesToProfile}
                        min={part.data.min}
                        max={part.data.max}
                        labels={part.data.labels}
                      />
                    </AnimatedCard>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }

    // For regular messages, use animated word-by-word reveal (skip for history)
    const formattedContent = formatResponse(content, conversationId, currentMission, onPlanEdit);

    if (isHistory) {
      // History mode: show content without animation, in chat bubble style
      return (
        <div className={styles.assistantMessageHistory}>
          <div
            className={styles.assistantContentHistory}
            dangerouslySetInnerHTML={{ __html: formattedContent }}
          />
        </div>
      );
    }

    return (
      <div className={styles.assistantMessage}>
        <AnimatedMessage
          content={formattedContent}
          onComplete={fireRevealComplete}
          skipAnimation={instantReveal}
        />
      </div>
    );
  }

  return null;
}

export default MessageBubble;
